import React, { useEffect, useState, useContext } from 'react';
import ApplicationPageTemplate from '../../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate';
import ProductContainer from './components/ProductContainer';
import { DragDropContext } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import { Button } from '@mui/material';

import { breadcrumbsActions } from "@store/breadcrumbs";
import { fetchCategory, selectMemoCategoryByCountry } from '@store/store-category-management';
import { getProducts, getAllProducts, addProduct, deleteProduct, updateOrder } from '@store/recommendation-products-management';
import Dropdown from 'src/components/Dropdown';
import DashboardEnvContext from "../../../../DashboardApp/DashboardEnvContext";
import { CountryList } from 'src/components/CountryList';
import useQuery from '@hooks/UseQuery';

const ALL_CATEGORY = 'all';

const columns = [
	{
		id: 'all',
		title: 'All Products',
		products: [],
	},
	{
		id: 'recommend',
		title: 'Recommended Products',
		products: [],
	},
];

const OptionContainer = styled.div`
	padding: 16px;
	display: flex;
	justify-content: space-between;
	align-items: flex-end;
`;

const FilterContainer = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
`;

const FilterItem = styled.div`
	margin-right: 10px;
	width: 200px;
`;

const DraggableContainer = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	padding: 0 16px;
	justify-content: space-between;
`;


const FilterLabel = styled.div`
	font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
	font-size: 0.9rem;
	font-weight: 500;
	margin: 0 0 5px 0;
`;

const ListProduct = () => {
	const query = useQuery()
	const countryCode = query.get("countryCode")

	const dispatch = useDispatch();
	const dashboard = useContext(DashboardEnvContext);

	// states
	const [isLoading, setIsLoading] = useState(false);
	const [selectedCategory, setSelectedCategory] = useState(null);
	const [productList, setProductList] = useState(columns);

	// redux stores
	const categoryStatus = useSelector((state) => state.giftCategoryList.status);
	const countryStatus = useSelector((state) => state.giftCountryList.status);
	const categoryByCountry = useSelector((state) => selectMemoCategoryByCountry(state, countryCode));
	const allProducts = useSelector(state => state.recommendationProductList.allProducts);
	const getProductStatus = useSelector(state => state.recommendationProductList.allProductStatus);
	const products = useSelector(state => state.recommendationProductList.products);
	const recommendationStatus = useSelector(state => state.recommendationProductList.status);

	useEffect(() => {
		dispatch(
			breadcrumbsActions.setBreadcrumbs([
				{ label: 'Home', url: '/' },
				{ label: 'Recommendation management', url: '/recommendation-management' },
				{ label: 'List', url: `/recommendation-management/list` },
			])
		);
		dispatch(fetchCategory());
	}, [dispatch]);

	useEffect(() => {
		if (!!countryCode) {
			dispatch(getProducts({ countryCode }));
			dispatch(getAllProducts({ countryCode  }));
		}
	}, [countryCode])

	useEffect(() => {
		initColumns()
	}, [products, allProducts]);

	const handleCountryChange = (country) => country;

	useEffect(() => {
		if (!!categoryByCountry) {
			setSelectedCategory(categoryByCountry[0].id);
		}
	}, [categoryByCountry]);

	const initColumns = () => {
		const productListState = [...productList];

		const productIds = {};
		for (const product of products) {
			productIds[product.productId] = product;
		}

		productListState[0].products = allProducts.filter(
			product => !productIds[product.id] && (
				selectedCategory === ALL_CATEGORY ||
				product.categories.includes(selectedCategory)
			)
		);

		productListState[1].products = allProducts.filter(
			product => !!productIds[product.id]
		).map(product => ({ ...product, recommendationInfo: productIds[product.id]}));

		productListState[1].products.sort((a, b) => a.recommendationInfo.sortingOrder - b.recommendationInfo.sortingOrder);

		setProductList([...productListState]);
	}

	useEffect(() => {
		if (!!selectedCategory) {
			const productListState = [...productList];

			const productIds = {};
			for (const product of productListState[1].products) {
				productIds[product.id] = product;
			}

			productListState[0].products = allProducts.filter(
				product => !productIds[product.id] && (
					selectedCategory === ALL_CATEGORY ||
					product.categories.includes(selectedCategory)
				)
			);

			setProductList([...productListState]);
		}
	}, [selectedCategory]);

	const refreshProduct = async () => {
		setIsLoading(true);
		const result = await dispatch(getProducts({ countryCode }));
		if (!getProducts.fulfilled.match(result)) {
			console.error(result.payload ? result.payload : result.error);
			window.location.reload();
		}
		setIsLoading(false);
	}

	const sortOrderInSameList = (payload) => {
		const { source, destination } = payload;
		if (!destination || !source) return null;

		const sourceIndex = source.droppableId;
		const destinationIndex = destination.droppableId;
		if (sourceIndex !== destinationIndex) return null;

		const column = productList[sourceIndex];
		const newProducts = [...column.products];
		const removedProduct = newProducts[source.index];
		const newState = [...productList];

		newProducts.splice(source.index, 1);
		newProducts.splice(destination.index, 0, removedProduct);

		newState[sourceIndex] = {
			...column,
			products: [...newProducts],
		};

		return newState;
	}

	const getSortOrderPayload = (productPayload) => (productPayload.map((product, index) => {
		if (!product.recommendationInfo) return null;
		return {
			id: product.recommendationInfo.id,
			sortingOrder: index + 1,
		}
	}).filter(e => e));

	const sortOrderAPI = async (payload) => {
		const result = await dispatch(updateOrder({ 
			recommendationProducts: payload 
		}));

		if (!updateOrder.fulfilled.match(result)) {
			if (result.payload) {
				dashboard.utils.showErrorNotification(`${result.payload.message}: ${result.payload.debugMessage}`);
			} else {
				dashboard.utils.showErrorNotification(`ERROR: ${result.error.message}`);
			}
		} else {
			dashboard.utils.showSuccessNotification(`Recommendation list updated!`);
		}

		await refreshProduct();
	}

	const updateProductsSortingOrder = async (payload) => {
		setIsLoading(true);
		const newState = sortOrderInSameList(payload);
		if (!newState) {
			setIsLoading(false);
			return;
		}
		
		const { source } = payload;
		const sourceIndex = source.droppableId;

		if (
			!newState[sourceIndex].products || 
			newState[sourceIndex].products.length === 0
		) {
			setIsLoading(false);
			return;
		}

		const sortPayload = getSortOrderPayload(newState[sourceIndex].products);

		await sortOrderAPI(sortPayload);
		setIsLoading(false);
	}

	const dragInSameList = (payload) => {
		const newState = sortOrderInSameList(payload);
		if (!newState) return;
		setProductList(newState);
	}

	const addRecommendationProduct = async (payload) => {
		setIsLoading(true);
		const { source, destination } = payload;
		const productSelected = productList[source.droppableId].products[source.index];
		const targetSequence = destination.index + 1;

		const result = await dispatch(addProduct({
			country: countryCode,
			productId: productSelected.id,
			sortingOrder: targetSequence,
		}));

		if (addProduct.fulfilled.match(result)) {
			dashboard.utils.showSuccessNotification(`Recommendation product list updated successfully!`);

			const sortedPayload = getSortOrderPayload(productList[1].products);
			sortedPayload.splice(
				targetSequence - 1, 
				0, 
				{
					id: result.payload.data.id,
					sortingOrder: targetSequence,
				}
			);

			const payload = sortedPayload.map((p, index) => ({
				id: p.id,
				sortingOrder: index + 1,
			}))

			await sortOrderAPI(payload);

		} else {
			console.error('result', result);
			if (result.payload) {
				dashboard.utils.showSuccessNotification(`${result.payload.message}: ${result.payload.debugMessage}`);
			} else {
				dashboard.utils.showSuccessNotification(`Add recommendation product error: ${result.error.message}`);
			}
		}

		setIsLoading(false);
	}

	const deleteRecommendationProduct = async (id) => {
		setIsLoading(true);
		const result = await dispatch(deleteProduct({ id }));
		if (!deleteProduct.fulfilled.match(result)) {
			if (result.payload) {
				dashboard.utils.showErrorNotification(`${result.payload.message}: ${result.payload.debugMessage}`);
			} else {
				dashboard.utils.showErrorNotification(`ERROR: ${result.error.message}`);
			}
		} else {
			await refreshProduct();
			dashboard.utils.showSuccessNotification(`Recommendation list updated!`);
		}
		setIsLoading(false);
	}

	const dragEndHandler = async (result) => {
		const { source, destination } = result;
		if (!destination || !source) return;
		if (
			source.droppableId === destination.droppableId &&
			source.index === destination.index
		) return;

		const sourceIndex = source.droppableId;
		const destinationIndex = destination.droppableId;

		if (sourceIndex === destinationIndex) {
			// sorting in same list
			if (sourceIndex === '0') {
				// sorting in all product list
				dragInSameList(result);
			} else {
				// udpate recommendation sorting list
				await updateProductsSortingOrder(result);
			}
		} else {
			if (sourceIndex === '0') {
				// dragging product from all product list to recommendation list
				await addRecommendationProduct(result);
			} else {
				// removing product from recommendation list
				const productSelected = productList[source.droppableId].products[source.index];
				await deleteRecommendationProduct(productSelected.recommendationInfo.id);
			}
		}
	};

	const handleCategoryChange = (e) => {
		if (!e) return;
		setSelectedCategory(e.id);
	};

	return (
		<ApplicationPageTemplate
			title={'Recommendation Mangement'}
			description={`Update recommended products`}
			pageReady={true}
		>
			<OptionContainer>
				<FilterContainer>
					<FilterItem>
						<FilterLabel>Country</FilterLabel>
						<CountryList
							required={true}
							fullWidth={true}
							margin='normal'
							onChange={handleCountryChange}
							placeholder="Select Country"
							id="country"
							value={countryCode}
							width="100%"
							background="white"
							disabled
						/>
					</FilterItem>
					<FilterItem>
						<FilterLabel>Category</FilterLabel>
						<Dropdown
							options={[...categoryByCountry].map((category) => ({
								id: category.id,
								name: category.title,
							}))}
							placeholder="Select Category"
							id="category"
							value={selectedCategory || undefined}
							style={{ width: '100%' }}
							disabled={categoryStatus === 'loading'}
							onSelectChange={handleCategoryChange}
							variant="outlined"
							background="white"
						/>
					</FilterItem>
				</FilterContainer>
			</OptionContainer>
			{categoryStatus !== 'loading' && countryStatus !== 'loading' && (
				<>
					<DraggableContainer>
						<DragDropContext onDragEnd={dragEndHandler}>
							{productList.map((column, index) => (
								<ProductContainer {...column} index={index} key={column.id} />
							))}
						</DragDropContext>
					</DraggableContainer>
				</>
			)}

			<Backdrop
				sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
				open={isLoading || categoryStatus === 'loading' || countryStatus === 'loading' || recommendationStatus === 'loading' || getProductStatus === 'loading'}
			>
				<CircularProgress color="inherit" />
			</Backdrop>
		</ApplicationPageTemplate>
	);
};

export default ListProduct;
